/**
 * Curating releases — create, edit, publish, delete — on either page.
 *
 * The folders page (`ReleasesPage`) and the release page (`ReleaseDetailPage`)
 * both offer these, and they must not drift: the same dialog, the same
 * "delete its versions first" refusal, the same server message shown
 * verbatim. So the state and the mutations live here, and each page renders
 * `dialogs` once and calls the openers from its own buttons.
 *
 * Curation controls follow `admin:users` (`canEdit`); the pages hide their
 * buttons for anyone else, and the BFF refuses regardless. Adding a document
 * to a version is a Documentation job and follows the docs authoring tiers
 * (`canAuthorDocs`), not `admin:users`.
 */
import { Alert } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState, type JSX } from "react";

import { useCan, useCanAny } from "../../../auth/permissions.js";
import { ConfirmDialog } from "../../../components/ui/confirm-dialog.js";
import { ApiError } from "../../../lib/api.js";
import { listProducts } from "../pricing/products/products-api.js";
import { ReleaseFormDialog } from "./ReleaseFormDialog.js";
import type { ReleaseFamily } from "./release-families.js";
import {
  type ReleaseDTO,
  type ReleaseInput,
  createRelease,
  deleteRelease,
  publishRelease,
  updateRelease,
} from "./releases-api.js";

const EMPTY_FORM: ReleaseInput = {
  version: "",
  status: "planned",
  description: "",
  release_notes: "",
};

/** Writing a guide onto a version is a Documentation job, so the control
 *  follows the docs authoring tiers rather than `admin:users`. */
const DOC_WRITE = ["documentation:create", "documentation:edit"] as const;

interface Options {
  families: ReleaseFamily[];
  invalidate: () => Promise<void>;
  /** Called after a delete goes through, with the row that is gone — the
   *  release page uses it to leave a release that no longer exists. */
  onDeleted?: (release: ReleaseDTO) => void;
}

export interface ReleaseCuration {
  canEdit: boolean;
  canAuthorDocs: boolean;
  /** New RELEASE — a folder. Two numbers, no parent. */
  openCreate: () => void;
  /** New VERSION inside a release: pre-filled with the release's number and
   *  filed under it explicitly. */
  openAddVersion: (line: ReleaseDTO) => void;
  openEdit: (release: ReleaseDTO) => void;
  publish: (release: ReleaseDTO) => void;
  requestDelete: (release: ReleaseDTO) => void;
  /** Render once per page. */
  dialogs: JSX.Element;
}

export function useReleaseCuration({ families, invalidate, onDeleted }: Options): ReleaseCuration {
  const canEdit = useCan("admin:users");
  const canAuthorDocs = useCanAny(DOC_WRITE);

  // Product picker for create — the same crm-api catalogue as CRM ▸ Pricing.
  const productsQuery = useQuery({
    queryKey: ["admin", "products"],
    queryFn: listProducts,
    enabled: canEdit,
  });
  const products = productsQuery.data ?? [];

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ReleaseDTO | null>(null);
  const [productId, setProductId] = useState<number | "">("");
  const [form, setForm] = useState<ReleaseInput>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ReleaseDTO | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const saveMut = useMutation({
    mutationFn: async () => {
      if (editing) {
        await updateRelease(editing.id, form);
      } else {
        // Send the picked product as the code/name a portal-api release
        // stores. `parent_release_id` rides along only when the operator
        // used a release's "New version"; otherwise the server files the
        // version under its own release.
        const product = products.find((p) => p.id === productId);
        await createRelease({
          ...form,
          ...(product?.code ? { product_code: product.code } : {}),
          ...(product?.name ? { product_name: product.name } : {}),
        });
      }
    },
    onSuccess: () => {
      setDialogOpen(false);
      setEditing(null);
      setForm(EMPTY_FORM);
      setProductId("");
      setFormError(null);
      void invalidate();
    },
    onError: (e: unknown) => {
      setFormError(e instanceof ApiError ? (e.message ?? "Save failed") : "Save failed");
    },
  });

  const publishMut = useMutation({
    mutationFn: (id: number) => publishRelease(id),
    onSuccess: () => void invalidate(),
  });

  function openCreate(): void {
    setEditing(null);
    setForm(EMPTY_FORM);
    setProductId(products[0]?.id ?? "");
    setFormError(null);
    setDialogOpen(true);
  }

  function openAddVersion(line: ReleaseDTO): void {
    setEditing(null);
    setForm({
      ...EMPTY_FORM,
      version: `${line.version}.`,
      parent_release_id: line.id,
    });
    const match = products.find((p) => p.name === line.product_name);
    setProductId(match?.id ?? products[0]?.id ?? "");
    setFormError(null);
    setDialogOpen(true);
  }

  function openEdit(r: ReleaseDTO): void {
    setEditing(r);
    setProductId(r.product_id ?? "");
    setForm({
      version: r.version,
      status: r.status,
      description: r.description ?? "",
      release_notes: r.release_notes ?? "",
    });
    setFormError(null);
    setDialogOpen(true);
  }

  function submit(): void {
    if (!editing && productId === "") {
      setFormError("Pick a product for this release.");
      return;
    }
    saveMut.mutate();
  }

  /**
   * Delete, and SAY WHY when the server refuses.
   *
   * portal-api will not delete a release while versions still sit inside it
   * (`RELEASE_LINE_HAS_BUILDS`) — deleting the folder would leave 10.2.0.27
   * standing alone with nothing to say where it came from. The server's
   * message is shown in the dialog rather than swallowed, so a refused delete
   * never looks like a button that did nothing.
   */
  async function confirmDelete(): Promise<void> {
    if (!pendingDelete) return;
    setDeleteError(null);
    try {
      await deleteRelease(pendingDelete.id);
    } catch (e) {
      setDeleteError(
        e instanceof ApiError && e.message ? e.message : "That release could not be deleted.",
      );
      throw e instanceof Error ? e : new Error("delete-failed");
    }
    const gone = pendingDelete;
    void invalidate();
    onDeleted?.(gone);
  }

  /** How many versions sit inside the release being deleted, if any. */
  const pendingDeleteVersions = pendingDelete
    ? (families.find((f) => f.line.id === pendingDelete.id && f.hasLine)?.builds.length ?? 0)
    : 0;

  const dialogs = (
    <>
      <ReleaseFormDialog
        open={dialogOpen}
        editing={editing}
        form={form}
        onFormChange={setForm}
        productId={productId}
        onProductChange={setProductId}
        products={products}
        error={formError}
        pending={saveMut.isPending}
        onClose={() => setDialogOpen(false)}
        onSubmit={submit}
      />
      <ConfirmDialog
        isOpen={pendingDelete !== null}
        onClose={() => {
          setPendingDelete(null);
          setDeleteError(null);
        }}
        title={pendingDeleteVersions > 0 ? "Delete its versions first" : "Delete release?"}
        description={
          pendingDeleteVersions > 0 ? (
            <>
              Release <strong>{pendingDelete?.version ?? ""}</strong> still holds{" "}
              <strong>{pendingDeleteVersions}</strong> version
              {pendingDeleteVersions === 1 ? "" : "s"}. Delete those first — removing the release
              while its versions ship would leave them with nothing to say where they came from.
            </>
          ) : (
            <>
              <strong>{pendingDelete?.version ?? ""}</strong> will be removed. This cannot be
              undone.
            </>
          )
        }
        confirmLabel={pendingDeleteVersions > 0 ? "Delete anyway" : "Delete"}
        onConfirm={confirmDelete}
      >
        {deleteError && (
          <Alert severity="error" sx={{ mt: 2, fontSize: 13 }}>
            {deleteError}
          </Alert>
        )}
      </ConfirmDialog>
    </>
  );

  return {
    canEdit,
    canAuthorDocs,
    openCreate,
    openAddVersion,
    openEdit,
    publish: (r) => publishMut.mutate(r.id),
    requestDelete: setPendingDelete,
    dialogs,
  };
}
